/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  StatusBar,
  Dimensions,
  Switch,
  TextInput,
  Modal,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {
  MaterialCommunityIcons,
  Ionicons,
  FontAwesome5,
} from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { LinearGradient } from 'expo-linear-gradient';
import { SafeAreaView } from 'react-native-safe-area-context';
import Slider from '@react-native-community/slider';

const { width, height } = Dimensions.get('window');

const ScholarshipFilter = ({ route }) => {
  const navigation = useNavigation();
  const [darkMode, setDarkMode] = useState(false);
  
  // Filter states
  const [selectedFilters, setSelectedFilters] = useState({
    status: [],
    type: [],
    category: [],
    location: [],
    academicLevel: [],
    competitionLevel: [],
  });
  
  // Range filters
  const [amountRange, setAmountRange] = useState({ min: 0, max: 100000 });
  const [durationRange, setDurationRange] = useState({ min: 0, max: 5 });
  const [deadlineRange, setDeadlineRange] = useState({ min: 0, max: 365 });
  
  // Saved filters
  const [savedFilters, setSavedFilters] = useState([]);
  const [saveModalVisible, setSaveModalVisible] = useState(false);
  const [filterName, setFilterName] = useState('');
  
  // Available options
  const filterOptions = {
    status: [
      { id: 'open', label: 'Currently Open', icon: 'lock-open' },
      { id: 'upcoming', label: 'Upcoming', icon: 'calendar-clock' },
      { id: 'closed', label: 'Closed', icon: 'lock' },
    ],
    type: [
      { id: 'merit', label: 'Merit-Based', icon: 'trophy' },
      { id: 'need', label: 'Need-Based', icon: 'heart' },
      { id: 'both', label: 'Merit & Need', icon: 'star-circle' },
    ],
    category: [
      { id: 'full', label: 'Full Scholarship', icon: 'cash' },
      { id: 'partial', label: 'Partial Scholarship', icon: 'cash-100' },
      { id: 'tuition', label: 'Tuition Only', icon: 'school' },
      { id: 'stipend', label: 'Stipend Only', icon: 'currency-usd' },
    ],
    location: [
      { id: 'uk', label: 'UK Only', icon: 'flag' },
      { id: 'international', label: 'International', icon: 'earth' },
      { id: 'england', label: 'England', icon: 'map-marker' },
      { id: 'scotland', label: 'Scotland', icon: 'map-marker' },
      { id: 'wales', label: 'Wales', icon: 'map-marker' },
      { id: 'northern_ireland', label: 'Northern Ireland', icon: 'map-marker' },
    ],
    academicLevel: [
      { id: 'undergraduate', label: 'Undergraduate', icon: 'school' },
      { id: 'masters', label: 'Master\'s', icon: 'school' },
      { id: 'phd', label: 'PhD', icon: 'microscope' },
      { id: 'postdoc', label: 'Postdoctoral', icon: 'flask' },
      { id: 'diploma', label: 'Diploma/Certificate', icon: 'certificate' },
    ],
    competitionLevel: [
      { id: 'low', label: 'Low', icon: 'trending-down', color: '#10b981' },
      { id: 'medium', label: 'Medium', icon: 'trending-neutral', color: '#f59e0b' },
      { id: 'high', label: 'High', icon: 'trending-up', color: '#3b82f6' },
      { id: 'very_high', label: 'Very High', icon: 'trending-up', color: '#ef4444' },
      { id: 'extremely_high', label: 'Extremely High', icon: 'fire', color: '#dc2626' },
    ],
  };

  const gradients = darkMode ? {
    primary: ['#2d3748', '#4a5568'],
    secondary: ['#805ad5', '#d53f8c'],
    success: ['#38a169', '#68d391'],
    warning: ['#d69e2e', '#ecc94b'],
    danger: ['#e53e3e', '#fc8181'],
    info: ['#3182ce', '#63b3ed'],
  } : {
    primary: ['#667eea', '#764ba2'],
    secondary: ['#f093fb', '#f5576c'],
    success: ['#4facfe', '#00f2fe'],
    warning: ['#43e97b', '#38f9d7'],
    danger: ['#fa709a', '#fee140'],
    info: ['#a8edea', '#fed6e3'],
  };

  useEffect(() => {
    loadInitialData();
    loadSavedFilters();
    
    // Load filters from route params if passed
    if (route.params?.filters) {
      setSelectedFilters(route.params.filters);
    }
  }, []);

  const loadInitialData = async () => {
    try {
      const darkModePref = await AsyncStorage.getItem('darkMode');
      if (darkModePref !== null) {
        setDarkMode(JSON.parse(darkModePref));
      }
    } catch (error) {
      console.error('Error loading dark mode preference:', error);
    }
  };

  const loadSavedFilters = async () => {
    try {
      const saved = await AsyncStorage.getItem('savedScholarshipFilters');
      if (saved) {
        setSavedFilters(JSON.parse(saved));
      }
    } catch (error) {
      console.error('Error loading saved filters:', error);
    }
  };

  const toggleFilter = (category, filterId) => {
    setSelectedFilters(prev => {
      const currentFilters = [...prev[category]];
      const index = currentFilters.indexOf(filterId);
      
      if (index > -1) {
        // Remove filter
        currentFilters.splice(index, 1);
      } else {
        // Add filter
        currentFilters.push(filterId);
      }
      
      return {
        ...prev,
        [category]: currentFilters,
      };
    });
  };

  const clearAllFilters = () => {
    setSelectedFilters({
      status: [],
      type: [],
      category: [],
      location: [],
      academicLevel: [],
      competitionLevel: [],
    });
    setAmountRange({ min: 0, max: 100000 });
    setDurationRange({ min: 0, max: 5 });
    setDeadlineRange({ min: 0, max: 365 });
  };

  const saveCurrentFilter = async () => {
    try {
      if (!filterName.trim()) {
        alert('Please enter a name for your filter');
        return;
      }

      const newFilter = {
        id: Date.now().toString(),
        name: filterName.trim(),
        filters: { ...selectedFilters },
        amountRange,
        durationRange,
        deadlineRange,
        date: new Date().toISOString(),
      };

      const updatedFilters = [...savedFilters, newFilter];
      setSavedFilters(updatedFilters);
      await AsyncStorage.setItem('savedScholarshipFilters', JSON.stringify(updatedFilters));
      
      setFilterName('');
      setSaveModalVisible(false);
      
      alert(`Filter "${filterName.trim()}" saved successfully!`);
    } catch (error) {
      console.error('Error saving filter:', error);
      alert('Failed to save filter');
    }
  };

  const deleteSavedFilter = async (filterId) => {
    try {
      const updatedFilters = savedFilters.filter(filter => filter.id !== filterId);
      setSavedFilters(updatedFilters);
      await AsyncStorage.setItem('savedScholarshipFilters', JSON.stringify(updatedFilters));
    } catch (error) {
      console.error('Error deleting filter:', error);
    }
  };

  const loadSavedFilter = (savedFilter) => {
    setSelectedFilters(savedFilter.filters);
    setAmountRange(savedFilter.amountRange);
    setDurationRange(savedFilter.durationRange);
    setDeadlineRange(savedFilter.deadlineRange);
  };

  const applyFilters = () => {
    navigation.navigate('Scholarships', {
      appliedFilters: {
        selectedFilters,
        amountRange,
        durationRange,
        deadlineRange,
      },
    });
  };

  const isFilterSelected = (category, filterId) => {
    return selectedFilters[category].includes(filterId);
  };

  const renderFilterSection = (title, options, category) => (
    <View style={styles.filterSection}>
      <Text style={styles.sectionTitle}>{title}</Text>
      <View style={styles.optionsContainer}>
        {options.map((option) => (
          <TouchableOpacity
            key={option.id}
            style={[
              styles.optionChip,
              isFilterSelected(category, option.id) && styles.optionChipSelected,
            ]}
            onPress={() => toggleFilter(category, option.id)}
          >
            <MaterialCommunityIcons
              name={option.icon}
              size={16}
              color={isFilterSelected(category, option.id) ? '#ffffff' : (darkMode ? '#a0aec0' : '#6b7280')}
            />
            <Text style={[
              styles.optionText,
              isFilterSelected(category, option.id) && styles.optionTextSelected,
            ]}>
              {option.label}
            </Text>
          </TouchableOpacity>
        ))}
      </View>
    </View>
  );

  const renderRangeFilter = (title, value, min, max, unit, onChange) => (
    <View style={styles.rangeSection}>
      <Text style={styles.sectionTitle}>{title}</Text>
      <View style={styles.rangeHeader}>
        <Text style={styles.rangeValue}>
          {value.min} - {value.max} {unit}
        </Text>
      </View>
      <View style={styles.rangeSliders}>
        <View style={styles.rangeSliderContainer}>
          <Text style={styles.rangeLabel}>Min: {value.min} {unit}</Text>
          <Slider
            style={styles.slider}
            minimumValue={min}
            maximumValue={max}
            value={value.min}
            onValueChange={(val) => onChange({ ...value, min: Math.round(val) })}
            minimumTrackTintColor="#667eea"
            maximumTrackTintColor={darkMode ? '#4a5568' : '#e5e7eb'}
            thumbTintColor="#667eea"
          />
        </View>
        <View style={styles.rangeSliderContainer}>
          <Text style={styles.rangeLabel}>Max: {value.max} {unit}</Text>
          <Slider
            style={styles.slider}
            minimumValue={min}
            maximumValue={max}
            value={value.max}
            onValueChange={(val) => onChange({ ...value, max: Math.round(val) })}
            minimumTrackTintColor="#667eea"
            maximumTrackTintColor={darkMode ? '#4a5568' : '#e5e7eb'}
            thumbTintColor="#667eea"
          />
        </View>
      </View>
    </View>
  );

  const renderSavedFilters = () => (
    <View style={styles.savedFiltersSection}>
      <View style={styles.savedFiltersHeader}>
        <Text style={styles.sectionTitle}>Saved Filters</Text>
        <TouchableOpacity onPress={() => setSaveModalVisible(true)}>
          <MaterialCommunityIcons name="content-save-plus" size={24} color="#667eea" />
        </TouchableOpacity>
      </View>
      
      {savedFilters.length === 0 ? (
        <Text style={styles.noSavedFiltersText}>No saved filters yet</Text>
      ) : (
        <ScrollView horizontal showsHorizontalScrollIndicator={false}>
          {savedFilters.map((filter) => (
            <TouchableOpacity
              key={filter.id}
              style={styles.savedFilterCard}
              onPress={() => loadSavedFilter(filter)}
            >
              <LinearGradient
                colors={gradients.info}
                style={styles.savedFilterGradient}
              >
                <TouchableOpacity
                  style={styles.deleteFilterButton}
                  onPress={() => deleteSavedFilter(filter.id)}
                >
                  <MaterialCommunityIcons name="close" size={16} color="#ffffff" />
                </TouchableOpacity>
                <MaterialCommunityIcons name="filter" size={24} color="#ffffff" />
                <Text style={styles.savedFilterName} numberOfLines={1}>{filter.name}</Text>
                <Text style={styles.savedFilterDate}>
                  {new Date(filter.date).toLocaleDateString()}
                </Text>
              </LinearGradient>
            </TouchableOpacity>
          ))}
        </ScrollView>
      )}
    </View>
  );

  const renderSaveFilterModal = () => (
    <Modal
      visible={saveModalVisible}
      animationType="slide"
      transparent={true}
      onRequestClose={() => setSaveModalVisible(false)}
    >
      <View style={styles.modalOverlay}>
        <View style={styles.modalContent}>
          <Text style={styles.modalTitle}>Save Current Filter</Text>
          
          <TextInput
            style={styles.filterNameInput}
            placeholder="Enter filter name"
            value={filterName}
            onChangeText={setFilterName}
            placeholderTextColor={darkMode ? '#a0aec0' : '#6b7280'}
            autoFocus={true}
          />
          
          <View style={styles.modalActions}>
            <TouchableOpacity
              style={[styles.modalButton, styles.cancelButton]}
              onPress={() => {
                setFilterName('');
                setSaveModalVisible(false);
              }}
            >
              <Text style={styles.cancelButtonText}>Cancel</Text>
            </TouchableOpacity>
            
            <TouchableOpacity
              style={[styles.modalButton, styles.saveButton]}
              onPress={saveCurrentFilter}
            >
              <Text style={styles.saveButtonText}>Save Filter</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );

  const styles = getStyles(darkMode);

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle={darkMode ? "light-content" : "light-content"} backgroundColor="#667eea" />
      
      {/* Header */}
      <LinearGradient
        colors={gradients.primary}
        style={styles.header}
      >
        <View style={styles.headerContent}>
          <TouchableOpacity 
            style={styles.backButton} 
            onPress={() => navigation.goBack()}
          >
            <MaterialCommunityIcons name="arrow-left" size={24} color="#ffffff" />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>🔍 Filter Scholarships</Text>
          <TouchableOpacity 
            style={styles.clearButton}
            onPress={clearAllFilters}
          >
            <Text style={styles.clearButtonText}>Clear All</Text>
          </TouchableOpacity>
        </View>
      </LinearGradient>

      <ScrollView 
        style={styles.container}
        showsVerticalScrollIndicator={false}
      >
        {renderFilterSection('Status', filterOptions.status, 'status')}
        {renderFilterSection('Type', filterOptions.type, 'type')}
        {renderFilterSection('Category', filterOptions.category, 'category')}
        {renderFilterSection('Location', filterOptions.location, 'location')}
        {renderFilterSection('Academic Level', filterOptions.academicLevel, 'academicLevel')}
        
        {/* Competition Level with custom colors */}
        <View style={styles.filterSection}>
          <Text style={styles.sectionTitle}>Competition Level</Text>
          <View style={styles.optionsContainer}>
            {filterOptions.competitionLevel.map((option) => (
              <TouchableOpacity
                key={option.id}
                style={[
                  styles.optionChip,
                  isFilterSelected('competitionLevel', option.id) && [styles.optionChipSelected, { backgroundColor: option.color }],
                ]}
                onPress={() => toggleFilter('competitionLevel', option.id)}
              >
                <MaterialCommunityIcons
                  name={option.icon}
                  size={16}
                  color={isFilterSelected('competitionLevel', option.id) ? '#ffffff' : (darkMode ? '#a0aec0' : '#6b7280')}
                />
                <Text style={[
                  styles.optionText,
                  isFilterSelected('competitionLevel', option.id) && styles.optionTextSelected,
                ]}>
                  {option.label}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>

        {renderRangeFilter(
          'Amount Range (£)',
          amountRange,
          0,
          100000,
          'GBP',
          setAmountRange
        )}

        {renderRangeFilter(
          'Duration (Years)',
          durationRange,
          0.5,
          5,
          'years',
          setDurationRange
        )}

        {renderRangeFilter(
          'Days Until Deadline',
          deadlineRange,
          0,
          365,
          'days',
          setDeadlineRange
        )}

        {renderSavedFilters()}
        
        <View style={styles.bottomSpacing} />
      </ScrollView>

      {/* Apply Button */}
      <LinearGradient
        colors={gradients.success}
        style={styles.applyButton}
      >
        <TouchableOpacity
          style={styles.applyButtonContent}
          onPress={applyFilters}
        >
          <MaterialCommunityIcons name="check-circle" size={24} color="#ffffff" />
          <Text style={styles.applyButtonText}>Apply Filters</Text>
          <View style={styles.filterCountBadge}>
            <Text style={styles.filterCountText}>
              {Object.values(selectedFilters).flat().length}
            </Text>
          </View>
        </TouchableOpacity>
      </LinearGradient>

      {renderSaveFilterModal()}
    </SafeAreaView>
  );
};

const getStyles = (darkMode) => StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: darkMode ? '#1a202c' : '#f0f4f8',
  },
  header: {
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomLeftRadius: 20,
    borderBottomRightRadius: 20,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backButton: {
    padding: 8,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#ffffff',
    flex: 1,
    textAlign: 'center',
  },
  clearButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    borderRadius: 8,
  },
  clearButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
  },
  container: {
    flex: 1,
    paddingHorizontal: 20,
  },
  filterSection: {
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    marginTop: 16,
    padding: 16,
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: darkMode ? '#f7fafc' : '#1f2937',
    marginBottom: 12,
  },
  optionsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  optionChip: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: darkMode ? '#4a5568' : '#f3f4f6',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: darkMode ? '#4a5568' : '#e5e7eb',
  },
  optionChipSelected: {
    backgroundColor: '#667eea',
    borderColor: '#667eea',
  },
  optionText: {
    marginLeft: 6,
    fontSize: 12,
    color: darkMode ? '#cbd5e0' : '#6b7280',
  },
  optionTextSelected: {
    color: '#ffffff',
    fontWeight: '600',
  },
  rangeSection: {
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    marginTop: 16,
    padding: 16,
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  rangeHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  rangeValue: {
    fontSize: 14,
    fontWeight: 'bold',
    color: darkMode ? '#f7fafc' : '#1f2937',
  },
  rangeSliders: {
    gap: 20,
  },
  rangeSliderContainer: {
    gap: 8,
  },
  slider: {
    width: '100%',
    height: 40,
  },
  rangeLabel: {
    fontSize: 12,
    color: darkMode ? '#a0aec0' : '#6b7280',
  },
  savedFiltersSection: {
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    marginTop: 16,
    padding: 16,
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  savedFiltersHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  noSavedFiltersText: {
    fontSize: 14,
    color: darkMode ? '#a0aec0' : '#6b7280',
    textAlign: 'center',
    paddingVertical: 20,
  },
  savedFilterCard: {
    width: 140,
    height: 140,
    marginRight: 12,
  },
  savedFilterGradient: {
    flex: 1,
    padding: 16,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
  },
  deleteFilterButton: {
    position: 'absolute',
    top: 8,
    right: 8,
    width: 24,
    height: 24,
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
  },
  savedFilterName: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#ffffff',
    marginTop: 8,
    textAlign: 'center',
  },
  savedFilterDate: {
    fontSize: 10,
    color: 'rgba(255, 255, 255, 0.8)',
    marginTop: 4,
  },
  applyButton: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    paddingVertical: 16,
    paddingHorizontal: 20,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: -2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 8,
  },
  applyButtonContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  applyButtonText: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#ffffff',
  },
  filterCountBadge: {
    position: 'absolute',
    right: 20,
    width: 28,
    height: 28,
    backgroundColor: '#ffffff',
    borderRadius: 14,
    justifyContent: 'center',
    alignItems: 'center',
  },
  filterCountText: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#667eea',
  },
  bottomSpacing: {
    height: 100,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    width: width * 0.8,
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    borderRadius: 16,
    padding: 24,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: darkMode ? '#f7fafc' : '#1f2937',
    marginBottom: 16,
    textAlign: 'center',
  },
  filterNameInput: {
    height: 48,
    borderWidth: 1,
    borderColor: darkMode ? '#4a5568' : '#e5e7eb',
    borderRadius: 8,
    paddingHorizontal: 12,
    fontSize: 16,
    color: darkMode ? '#f7fafc' : '#1f2937',
    backgroundColor: darkMode ? '#1a202c' : '#f9fafb',
    marginBottom: 24,
  },
  modalActions: {
    flexDirection: 'row',
    gap: 12,
  },
  modalButton: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 8,
    alignItems: 'center',
  },
  cancelButton: {
    backgroundColor: darkMode ? '#4a5568' : '#f3f4f6',
  },
  saveButton: {
    backgroundColor: '#667eea',
  },
  cancelButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: darkMode ? '#cbd5e0' : '#6b7280',
  },
  saveButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#ffffff',
  },
});

export default ScholarshipFilter;